import { getHistory, FieldType, ViewModelBase, ApiResult } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";

import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { ProjectGeneralViewModel } from "Views/Project/General/ProjectGeneralViewModel";
import { StoresInstance } from "Globals/Stores/Stores";
import moment from "moment";
import { AppUrls } from "AppUrls";
import { ProjectsAllReportModel, ProjectsReportRequestDTO, ProjectsReportResponseDTO, Projects_DTO } from "./ProjectsAllReportModel";
import { csvAxiosRequestConfig, exportCSV } from "Utils/Utils";

export class ProjectsAllReportViewModel extends ViewModelBase<ProjectsAllReportModel> {
    // #region Constructors and Disposers

    constructor() {
        super(new ProjectsAllReportModel());

        this.setDecorators(ProjectsAllReportViewModel);
    }

    @observable public topGridModel: Projects_DTO | null = null;
    @observable public bottomGridModel: Projects_DTO | null = null;

    @observable public canExportCSV: boolean = false;

    @action
    public setCanExportCSV(val: boolean) {
        this.canExportCSV = val;
    }

    @computed
    public get canDeleteIE(): boolean {
        return ProjectGeneralViewModel.Instance.isDraft && StoresInstance.Domain.AccountStore.getCanEditIE;
    }

    @computed
    public get canEditIE(): boolean {
        return StoresInstance.Domain.AccountStore.getCanEditIE;
    }

    @computed
    public get getTopDateFormatted(): string {
        return this.model.reportEndDateTop ? moment(this.model.reportEndDateTop).format("DD/MM/YYYY").toString() : "Please select a date";
    }

    @computed
    public get getBottomDateFormatted(): string {
        return this.model.reportEndDateBottom ? moment(this.model.reportEndDateBottom).format("DD/MM/YYYY").toString() : "Please select a date";
    }

    // #region Server Actions

    @action
    public runReport = async (): Promise<ApiResult<ProjectsReportResponseDTO>> => {
        this.setIsLoading(true);
        const toDateBottom = this.model.reportEndDateBottom;
        const toDateTop = this.model.reportEndDateTop;
        const request: ProjectsReportRequestDTO = {
            toDateBottom: toDateBottom,
            toDateTop: toDateTop,
        };
        let apiResult = await this.Post<ProjectsReportResponseDTO>(AppUrls.Server.Report.AllProjects.GetProjectsReport, request);
        if (apiResult) {
            if (apiResult.wasSuccessful) {
                runInAction(() => {
                    this.topGridModel = apiResult.payload.projectGridTop;
                    this.bottomGridModel = apiResult.payload.projectGridBottom;
                });
            } else {
                console.log(apiResult.errors);
            }
        }
        this.setIsLoading(false);
        return apiResult;
    };

    @action
    public generateReportCSV = async () => {
        this.setIsLoading(true);

        // JC: Download a CSV file using a HTTP POST request.
        // Source: https://stackoverflow.com/a/55138366

        const toDateBottom = this.model.reportEndDateBottom;
        const toDateTop = this.model.reportEndDateTop;
        const request: ProjectsReportRequestDTO = {
            toDateBottom: toDateBottom,
            toDateTop: toDateTop,
        };

        await exportCSV(AppUrls.Server.Report.AllProjects.GenerateAllProjectsReportCSV, request, await this.getConfig(true, csvAxiosRequestConfig)).finally(() =>
            this.setIsLoading(false),
        );
    };

    public server: ServerViewModel = new ServerViewModel();

    @action
    public reset = () => {
        this.model.reset();
        this.server.reset();
    };

    @action
    public handleCancel = (): void => {
        getHistory().goBack();
    };

    // #endregion Client Actions

    // #region Boilerplate

    public async isFieldValid(fieldName: keyof FieldType<ProjectsAllReportModel>): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    // #region Snackbar

    @observable
    public snackbarState = false;

    @action
    public setSnackbarState = (val: boolean) => {
        this.snackbarState = val;
    };

    @observable
    public snackMessage = "";

    @action
    public setSnackMessage = (val: string) => {
        this.snackMessage = val;
    };

    @observable
    public snackType = "";

    @action
    public setSnackType = (val: string) => {
        this.snackType = val;
    };

    @observable
    public SNACKSUCCESS = "success";

    @observable
    public SNACKERROR = "error";
    // #endregion

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    // #endregion Boilerplate
}
